import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ContactRow } from "../lib/db";
import { newFrameId } from "../lib/frame";
import { VoiceCallSession, type VoiceCallUi } from "../lib/webrtcCall";

type SendRealtimeCommand = (type: string, payload: Record<string, unknown>, id?: string) => boolean;

type UseVoiceCallSessionArgs = {
  keysUserId: string | null;
  isWsLeader: boolean;
  contacts: ContactRow[];
  sendRealtimeCommand: SendRealtimeCommand;
};

export function useVoiceCallSession({
  keysUserId,
  isWsLeader,
  contacts,
  sendRealtimeCommand,
}: UseVoiceCallSessionArgs) {
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const voiceSessionRef = useRef<VoiceCallSession | null>(null);
  const contactsRef = useRef(contacts);
  const sendRef = useRef(sendRealtimeCommand);
  const [voiceUi, setVoiceUi] = useState<VoiceCallUi>({ phase: "idle" });

  contactsRef.current = contacts;
  sendRef.current = sendRealtimeCommand;

  useEffect(() => {
    if (!keysUserId || !isWsLeader) {
      voiceSessionRef.current?.destroy();
      voiceSessionRef.current = null;
      setVoiceUi({ phase: "idle" });
      return;
    }

    const session = new VoiceCallSession(
      (peerId, callId, signal) => {
        sendRef.current("call.signal", { peer_id: peerId, call_id: callId, signal }, newFrameId());
      },
      (peerId, callId) => {
        sendRef.current("call.hangup", { peer_id: peerId, call_id: callId }, newFrameId());
      },
      setVoiceUi,
      (id) => contactsRef.current.find((c) => c.user_id === id)?.username,
    );
    voiceSessionRef.current = session;
    session.setRemoteAudioElement(remoteAudioRef.current);
    return () => {
      session.destroy();
      voiceSessionRef.current = null;
    };
  }, [isWsLeader, keysUserId]);

  /** `<audio>` mounts after the session effect; keep the session pointed at the real element. */
  useLayoutEffect(() => {
    voiceSessionRef.current?.setRemoteAudioElement(remoteAudioRef.current);
  });

  const startVoiceCall = useCallback((peerId: string | null) => {
    if (!peerId) return;
    void voiceSessionRef.current?.startOutgoing(peerId);
  }, []);

  return {
    voiceUi,
    setVoiceUi,
    voiceSessionRef,
    remoteAudioRef,
    startVoiceCall,
  };
}
